import { IGenericPoliciesTableData, IPolicyFilterLovs } from '.';
import { IAbstractRecord } from '@models';
import { IListingData } from '@models/listing';
import { capitalizeFirstLetter } from '@utils/formatting-utils';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';

export function mapToListingData(data: IAbstractRecord): IListingData {
  const list: IAbstractRecord = {};

  data.Production.queries.getLatestPolicyList.items.forEach(
    (obj: IGenericPoliciesTableData, i: number) => {
      const subline =
        obj.production_Policy_SublineID?.views?.PlanConfigManagement_all
          ?.properties;
      const line =
        obj.production_Policy_LineID?.views?.PlanConfigManagement_all
          ?.properties;
      const plan =
        obj.production_Policy_PlanID?.views?.PlanConfigManagement_all
          ?.properties;
      const insured =
        obj.production_Policy_InsuredID?.views?.Production_all?.properties;

      list[i] = {
        id: obj.production_Policy_Id,
        production_Policy_PolicyNumber: obj.production_Policy_PolicyNumber,
        production_Policy_PolicyName: obj.production_Policy_PolicyName,
        line_ExternalCode: `${line.ExternalCode} - ${line.Name}`,
        subline_Name: `${subline.ExternalCode} - ${subline.Name}`,
        plan_Name: `${plan.ExternalCode} - ${plan.Name}`,
        policyPerson_FullName: insured?.FullName,
        production_Policy_Status: capitalizeFirstLetter(
          obj.production_Policy_Status?.replaceAll('_', ' ')
        ),
        production_Policy_StatusReason:
          obj?.production_Policy_StatusReason?.Title,
        statusReasonTitle: obj?.production_Policy_StatusReason?.Title,
        production_Policy_PolicyEffectiveDate:
          obj.production_Policy_PolicyEffectiveDate,
        production_Policy_PolicyIssueDate:
          obj.production_Policy_PolicyIssueDate,
      };
    }
  );

  return {
    pagedItems: list,
    pageSize: data.Production.queries.getLatestPolicyList?.paging?.pageSize,
    pageNumber: data.Production.queries.getLatestPolicyList?.paging?.pageNumber,
    totalCount: data.Production.queries.getLatestPolicyList?.paging?.totalCount,
  };
}

interface IPerson {
  salesforceManagement_Person_Id: string;
  salesforceManagement_Person_FullName: string;
  salesforceManagement_Person_PersonCode: string;
}

export function dataToLovs(
  data: IAbstractRecord,
  linesData: IAbstractRecord,
  filterV: IAbstractRecord
): IPolicyFilterLovs {
  const lovs: IPolicyFilterLovs = {
    line: {},
    subline: {},
    plan: {},
    insured: {},
    status: {},
    statusReason: {},
  };

  lovs.line = dataToLinesLovs(linesData);
  lovs.subline = dataToSublineLovs(data);
  lovs.plan = dataToPlanLovs(data, filterV.subline, lovs.subline);

  data?.SalesforceManagement?.queries?.AllActivePersons?.sort(
    (a: IPerson, b: IPerson) => {
      const codeA = a.salesforceManagement_Person_PersonCode;
      const codeB = b.salesforceManagement_Person_PersonCode;
      return codeA.localeCompare(codeB);
    }
  )?.forEach((obj: IPerson) => {
    lovs.insured[obj.salesforceManagement_Person_Id] =
      `${obj.salesforceManagement_Person_PersonCode} - ${obj.salesforceManagement_Person_FullName}`;
  });

  lovs.status = enumListAsRecordObject(
    data?.Production_PolicyStatuses?.enumValues
  );

  lovs.statusReason = lookupListAsRecordObject(
    data?.Production?.lookups?.policyStatusReasons
  );

  return lovs;
}

interface IPlan {
  salesforceManagement_AssignedBPPlan_PlanID: {
    id: string;
    views: {
      PlanConfigManagement_all: {
        properties: {
          Name: string;
          ExternalCode: string;
          SublineID: {
            id: string;
            views: {
              PlanConfigManagement_all: {
                properties: {
                  Name: string;
                  ExternalCode: string;
                };
              };
            };
          };
        };
      };
    };
  };
}

export function dataToSublineLovs(data: IAbstractRecord) {
  const sublines: Record<string, string> = {};

  data?.SalesforceManagement?.queries?.getDistinctAssignedBPPlanListWithoutPagination
    ?.sort((a: IPlan, b: IPlan) => {
      const codeA =
        a.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.SublineID?.views
          ?.PlanConfigManagement_all?.properties?.ExternalCode;
      const codeB =
        b.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.SublineID?.views
          ?.PlanConfigManagement_all?.properties?.ExternalCode;
      return codeA.localeCompare(codeB);
    })
    ?.forEach((obj: IPlan) => {
      sublines[
        obj?.salesforceManagement_AssignedBPPlan_PlanID?.views?.PlanConfigManagement_all?.properties?.SublineID?.id
      ] =
        `${obj?.salesforceManagement_AssignedBPPlan_PlanID?.views?.PlanConfigManagement_all?.properties?.SublineID?.views?.PlanConfigManagement_all?.properties?.ExternalCode} - ${obj.salesforceManagement_AssignedBPPlan_PlanID?.views?.PlanConfigManagement_all?.properties?.SublineID?.views?.PlanConfigManagement_all?.properties?.Name}`;
    });

  return sublines;
}

export function dataToPlanLovs(
  data: IAbstractRecord,
  sublineValue: string,
  sublines: Record<string, string>
) {
  const plans: Record<string, string> = {};

  data?.SalesforceManagement?.queries?.getDistinctAssignedBPPlanListWithoutPagination
    ?.sort((a: IPlan, b: IPlan) => {
      const codeA =
        a.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.ExternalCode;
      const codeB =
        b.salesforceManagement_AssignedBPPlan_PlanID.views
          .PlanConfigManagement_all.properties.ExternalCode;
      return codeA.localeCompare(codeB);
    })
    ?.forEach((obj: IPlan) => {
      if (!sublineValue || sublineValue?.length === 0) {
        if (
          sublines[
            obj?.salesforceManagement_AssignedBPPlan_PlanID?.views
              ?.PlanConfigManagement_all?.properties?.SublineID?.id
          ]
        ) {
          plans[obj?.salesforceManagement_AssignedBPPlan_PlanID?.id] =
            `${obj?.salesforceManagement_AssignedBPPlan_PlanID?.views?.PlanConfigManagement_all?.properties?.ExternalCode} - ${obj.salesforceManagement_AssignedBPPlan_PlanID?.views?.PlanConfigManagement_all?.properties?.Name}`;
        }
      } else {
        if (
          sublineValue?.includes(
            obj?.salesforceManagement_AssignedBPPlan_PlanID?.views
              ?.PlanConfigManagement_all?.properties?.SublineID?.id
          )
        ) {
          plans[obj?.salesforceManagement_AssignedBPPlan_PlanID?.id] =
            `${obj?.salesforceManagement_AssignedBPPlan_PlanID?.views?.PlanConfigManagement_all?.properties?.ExternalCode} - ${obj.salesforceManagement_AssignedBPPlan_PlanID?.views?.PlanConfigManagement_all?.properties?.Name}`;
        }
      }
    });

  return plans;
}

interface ILine {
  salesforceManagement_AssignedBPPlan_LineID: {
    views: {
      PlanConfigManagement_all: {
        properties: {
          Id: string;
          Name: string;
          ExternalCode: string;
        };
      };
    };
  };
}

export function dataToLinesLovs(linesData: IAbstractRecord) {
  const lines: Record<string, string> = {};

  linesData?.SalesforceManagement?.queries?.getDistinctAssignedBPLineListWithoutPagination
    ?.sort((a: ILine, b: ILine) => {
      const codeA = Number(
        a.salesforceManagement_AssignedBPPlan_LineID.views
          .PlanConfigManagement_all.properties.ExternalCode
      );
      const codeB = Number(
        b.salesforceManagement_AssignedBPPlan_LineID.views
          .PlanConfigManagement_all.properties.ExternalCode
      );
      return codeA - codeB;
    })
    .forEach((obj: ILine) => {
      lines[
        obj?.salesforceManagement_AssignedBPPlan_LineID?.views?.PlanConfigManagement_all?.properties?.Id
      ] =
        `${obj?.salesforceManagement_AssignedBPPlan_LineID?.views?.PlanConfigManagement_all?.properties?.ExternalCode} - ${obj?.salesforceManagement_AssignedBPPlan_LineID?.views?.PlanConfigManagement_all?.properties?.Name}`;
    });

  return lines;
}

export function extractAssignedBPWithPagination(data: any) {
  const assignedBP: Record<string, string> = {};

  data.SalesforceManagement.queries.getAssignedBPList.items.forEach(
    (element: {
      businessPartner_Id: string;
      salesforceManagement_AssignedBP_BusinessPartnerID: {
        views: {
          SalesforceManagement_all: {
            properties: {
              Code: string;
              PersonID: {
                views: {
                  SalesforceManagement_all: {
                    properties: {
                      FullName: string;
                    };
                  };
                };
              };
            };
          };
        };
      };
    }) => {
      assignedBP[element.businessPartner_Id] =
        `${element.salesforceManagement_AssignedBP_BusinessPartnerID.views.SalesforceManagement_all.properties.Code} - ${element.salesforceManagement_AssignedBP_BusinessPartnerID.views.SalesforceManagement_all.properties.PersonID.views.SalesforceManagement_all.properties.FullName}`;
    }
  );

  return assignedBP;
}
